import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@simplicity/ui";
import type { SaveEvent } from "./clusterRevisions";
import { diffJson, type DiffEntry } from "./diffJson";
import { SqlMergeView } from "./SqlMergeView";

interface TransformDiffSheetProps {
  saveEvent: SaveEvent | null;
  currentSqlTemplate: string;
  currentParams: Record<string, string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (sqlTemplate: string, params: Record<string, string>) => void;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getPreviousSql(previousValue: Record<string, unknown> | undefined): string {
  if (!previousValue) return "";
  // Snapshot was wrapped as { value: <sqlTemplate> }
  if (typeof previousValue.value === "string") return previousValue.value;
  // Defensive: handle a future shape that uses { sqlTemplate: <...> }
  if (typeof previousValue.sqlTemplate === "string") return previousValue.sqlTemplate;
  return "";
}

function getPreviousParams(
  previousValue: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!previousValue) return {};
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(previousValue)) {
    // SIM-1540: avoid the `[object Object]` fallback by JSON-encoding non-string
    // values. previousValue is `Record<string, unknown>` from saved config —
    // most entries are primitive in practice but the type is unconstrained.
    if (typeof val === "string") {
      result[key] = val;
    } else if (val === null || val === undefined) {
      result[key] = "";
    } else if (typeof val === "number" || typeof val === "boolean" || typeof val === "bigint") {
      result[key] = String(val);
    } else {
      result[key] = JSON.stringify(val);
    }
  }
  return result;
}

function ParamsDiffRow({ entry }: { entry: DiffEntry }) {
  if (entry.kind === "added") {
    return (
      <span className="text-success">
        + {entry.path}: {JSON.stringify(entry.current)}
      </span>
    );
  }
  if (entry.kind === "removed") {
    return (
      <span className="text-destructive">
        − {entry.path}: {JSON.stringify(entry.previous)}
      </span>
    );
  }
  return (
    <span>
      {entry.path}: <span className="text-destructive">{JSON.stringify(entry.previous)}</span> →{" "}
      <span className="text-success">{JSON.stringify(entry.current)}</span>
    </span>
  );
}

function SqlDiffSection({
  previousSql,
  currentSqlTemplate,
  sqlChanged,
}: {
  previousSql: string;
  currentSqlTemplate: string;
  sqlChanged: boolean;
}) {
  return (
    <section className="flex min-h-[300px] flex-1 flex-col gap-2">
      <h3 className="text-sm font-medium">SQL template</h3>
      {sqlChanged ? (
        <div className="flex-1 overflow-hidden">
          <SqlMergeView previous={previousSql} current={currentSqlTemplate} />
        </div>
      ) : (
        <p className="text-xs italic text-muted-foreground">
          No changes to SQL between this revision and current.
        </p>
      )}
    </section>
  );
}

function ParamsDiffSection({ paramsDiff }: { paramsDiff: DiffEntry[] }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Params</h3>
      {paramsDiff.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">
          No changes to params between this revision and current.
        </p>
      ) : (
        <ul className="space-y-1 text-xs">
          {paramsDiff.map((entry, i) => (
            <li key={`${entry.kind}-${entry.path}-${i}`} className="font-mono break-all">
              <ParamsDiffRow entry={entry} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function TransformDiffSheet({
  saveEvent,
  currentSqlTemplate,
  currentParams,
  open,
  onOpenChange,
  onRestore,
}: TransformDiffSheetProps) {
  const sqlRevision = saveEvent?.revisions.find((r) => r.field === "sqlTemplate");
  const paramsRevision = saveEvent?.revisions.find((r) => r.field === "params");

  const previousSql = getPreviousSql(sqlRevision?.previousValue);
  const previousParams = getPreviousParams(paramsRevision?.previousValue);
  const paramsDiff = diffJson(previousParams, currentParams);
  const sqlChanged = sqlRevision !== undefined && previousSql !== currentSqlTemplate;

  const handleRestore = () => {
    onRestore(previousSql, previousParams);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-[80vw]">
        <SheetHeader>
          <SheetTitle>Save event</SheetTitle>
          {saveEvent && (
            <SheetDescription>
              {formatTimestamp(saveEvent.createdAt)}
              {saveEvent.createdByEmail ? ` · ${saveEvent.createdByEmail}` : " · system"}
            </SheetDescription>
          )}
        </SheetHeader>

        {saveEvent && (
          <div className="flex flex-1 flex-col gap-6 overflow-hidden">
            {sqlRevision && (
              <SqlDiffSection
                previousSql={previousSql}
                currentSqlTemplate={currentSqlTemplate}
                sqlChanged={sqlChanged}
              />
            )}
            {paramsRevision && <ParamsDiffSection paramsDiff={paramsDiff} />}
          </div>
        )}

        <div className="mt-auto flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button type="button" onClick={handleRestore} disabled={!saveEvent}>
            Restore this version
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
